import React, { useState, useEffect, useMemo } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import { getMainAxios } from '../utils/util';

HighchartsAccessibility(Highcharts);

export type ChartPeriod = '1m' | '3m' | '5m' | '10m' | '30m' | '60m' | 'D' | 'W' | 'M';

const PERIOD_TO_MINUTE: Record<ChartPeriod, number> = {
  '1m': 1, '3m': 3, '5m': 5, '10m': 10, '30m': 30, '60m': 60,
  'D': 0, 'W': 0, 'M': 0,
};

// 주문·체결 표식 색. 캔들(상승 빨강 / 하락 파랑)과 겹치지 않는 색을 쓴다.
const MARKER_BUY  = '#059669';   // 초록 — 매수
const MARKER_SELL = '#ea580c';   // 주황 — 매도

// 이동평균선 — 종목차트 공통. 봉 종류(분/일/주/월)와 무관하게 같은 기간을 쓴다.
const MA_DEFS: { period: number; color: string }[] = [
  { period: 5,   color: '#ef4444' },
  { period: 10,  color: '#f59e0b' },
  { period: 20,  color: '#22c55e' },
  { period: 60,  color: '#3b82f6' },
  { period: 120, color: '#a855f7' },
  { period: 240, color: '#64748b' },
];

/**
 * 단순이동평균(SMA). 캔들 데이터([t, o, h, l, c])의 종가 기준.
 *
 * Highcharts 의 `sma` indicator 모듈을 쓰지 않는 이유:
 *   - 별도 모듈(indicators/indicators.js) 로드가 필요하고
 *   - linkedTo 로 원본 시리즈에 묶여야 해서 시리즈 구성이 복잡해진다.
 * 봉 수가 수천 개 수준이라 직접 계산해도 부담이 없다.
 *
 * 앞의 (period-1) 개는 값이 없으므로 null 을 넣어 선이 끊기게 한다.
 */
const calcSMA = (candle: number[][], period: number): (number | null)[][] => {
  if (candle.length < period) return [];
  const out: (number | null)[][] = [];
  let sum = 0;
  for (let i = 0; i < candle.length; i++) {
    sum += candle[i][4];                       // 종가
    if (i >= period) sum -= candle[i - period][4];
    out.push([candle[i][0], i >= period - 1 ? sum / period : null]);
  }
  return out;
};

interface DailyRow { date: string; o: number; h: number; l: number; c: number; v: number; }
interface IntradayBar { d: string; t: string; o: number; h: number; l: number; c: number; v: number; }

/** 차트에 찍을 주문/체결 표식. 호출부가 자기 데이터를 이 모양으로 넘긴다. */
export interface TradeMarker {
  /** "yyyy-MM-dd HH:mm[:ss]" 또는 ISO. 분 단위로 내림해 봉에 맞춘다. */
  t: string;
  /** 가격. 없으면 표식을 찍지 않는다(가격축 위치를 못 정한다). */
  prc?: number | null;
  qty?: number | null;
  /** true=매수, false=매도 */
  isBuy: boolean;
  /** 체결이면 true. 주문(미체결 포함)이면 false. */
  isFill: boolean;
  /** 툴팁 추가 설명 (상태·에러 등) */
  note?: string | null;
}

interface Props {
  prodId: string;
  prodNm?: string;
  height?: number;
  /**
   * 주문·체결 표식 (선택). 넘기면 캔들 위에 ▲매수/▼매도 를 찍는다.
   * 넘기지 않으면 종전과 동일하게 동작한다.
   */
  markers?: TradeMarker[];
  /**
   * 분봉을 **이 날짜 하루만** 보여준다 ("yyyy-MM-dd"). 미지정이면 종전처럼
   * period 별 기간(1~5분=7일 등)을 통째로 그린다.
   *
   * <para>포트폴리오 화면처럼 "그날 주문·체결"을 보는 용도에는 하루만 필요하다.
   * 여러 날을 그리면 x축에 09:00~15:00 이 날짜 수만큼 반복돼
   * 같은 시각이 중복돼 보인다 (2026-10-06 사용자 지적).</para>
   */
  date?: string;
}

const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const todayStr = () => fmtDate(new Date());
const daysAgoStr = (days: number) => fmtDate(new Date(Date.now() - days * 86400000));

// period 별 자동 조회 기간 (캘린더 일수 기준)
const PERIOD_TO_DAYS: Record<ChartPeriod, number> = {
  '1m': 7,  '3m': 7,  '5m': 7,           // 단주기 분봉: 영업일 5일 ≈ 7일
  '10m': 30, '30m': 30, '60m': 30,       // 장주기 분봉: 30일
  'D': 365,                               // 일봉 1년
  'W': 365 * 5,                           // 주봉 5년
  'M': 365 * 10,                          // 월봉 10년
};

/** 공통 종목 차트 — 분(1/3/5/10/30/60분) / 일 / 주 / 월 토글 + 캔들 + 거래량 */
const StockChart: React.FC<Props> = ({ prodId, prodNm, height = 400, markers, date }) => {
  // 주문·체결 표식을 받은 경우 기본을 1분봉으로 둔다.
  // 일봉(기본값)이면 1년치가 그려져 하루 안의 표식이 한 점에 뭉쳐 안 보인다.
  //
  // ⚠ useState 초기값만으로는 부족하다 — 마운트 시점엔 체결이 아직 로드 전이라
  //   markers 가 비어 'D' 로 굳는다. 표식이 **나중에 도착**하면 그때 1분봉으로
  //   바꿔준다 (사용자가 직접 period 를 고른 뒤에는 건드리지 않는다).
  const [period, setPeriod] = useState<ChartPeriod>(markers?.length ? '1m' : 'D');
  const [userPickedPeriod, setUserPickedPeriod] = useState(false);

  useEffect(() => {
    if (userPickedPeriod) return;
    if (markers?.length && period === 'D') setPeriod('1m');
  }, [markers, period, userPickedPeriod]);
  const [data, setData] = useState<{ candle: number[][]; volume: number[][] }>({ candle: [], volume: [] });
  const [loading, setLoading] = useState(false);
  const axiosInstance = getMainAxios();

  const isIntraday = PERIOD_TO_MINUTE[period] > 0;
  // period 변경 시 자동 계산되는 기간
  const days = PERIOD_TO_DAYS[period];
  // date 가 오면 분봉은 그 하루만 본다. 일/주/월봉은 추세를 봐야 하므로 종전대로.
  const useOneDay = !!date && PERIOD_TO_MINUTE[period] > 0;
  const from = useOneDay ? date! : daysAgoStr(days);
  const to = useOneDay ? date! : todayStr();

  /**
   * 일자 문자열(yyyy-MM-dd) → epoch ms. **UTC 자정 기준.**
   *
   * `new Date('2026-10-02T00:00:00')` 는 브라우저 로컬(KST) 자정으로 해석돼
   * 2026-10-01T15:00Z 가 된다. 차트 x축은 UTC 라, 일봉이 전부 **하루 앞으로**
   * 밀려 찍혔다 — 10-02 봉이 10-01 자리에 가고 마지막 날이 사라진 것처럼 보였다.
   * 분봉은 `krxBarToEpoch` 가 Date.UTC 를 쓰는데 일봉만 로컬이라,
   * 체결 표식과 캔들이 서로 다른 기준 위에 놓여 있었다 (2026-10-06 실측).
   */
  const dayToEpoch = (d: string): number => {
    const [yy, mo, dd] = d.split('-').map(Number);
    return Date.UTC(yy, mo - 1, dd);
  };

  // KRX 시간대 분봉 → epoch ms
  const krxBarToEpoch = (d: string, t: string): number => {
    const yy = parseInt(d.substring(0, 4));
    const mo = parseInt(d.substring(4, 6)) - 1;
    const dd = parseInt(d.substring(6, 8));
    const hh = parseInt(t.substring(0, 2));
    const mm = parseInt(t.substring(2, 4));
    return Date.UTC(yy, mo, dd, hh - 9, mm); // KST → UTC
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (isIntraday) {
        // 1/3/5/10/30/60분봉 — 모두 동일 패턴: minute 파라미터만 다름
        const minute = PERIOD_TO_MINUTE[period];
        const r = await axiosInstance.get('/api/v1/quant/intraday-bars', {
          params: { prodId, from, to, minute }
        });
        const bars: IntradayBar[] = r.data || [];
        const candle = bars.map(b => [krxBarToEpoch(b.d, b.t), b.o, b.h, b.l, b.c]);
        const volume = bars.map(b => [krxBarToEpoch(b.d, b.t), b.v]);
        setData({ candle, volume });
      } else {
        // 일봉 → 일/주/월 (사용자 입력 기간 필터)
        const r = await axiosInstance.get('/api/v1/quant/screener/history', { params: { prodId } });
        const daily: DailyRow[] = r.data || [];
        const fromMs = dayToEpoch(from);
        const toMs = dayToEpoch(to) + 86400000 - 1;
        const dailyBars = daily
          .map(d => ({
            t: dayToEpoch(d.date),
            o: d.o, h: d.h, l: d.l, c: d.c, v: d.v,
          }))
          .filter(b => b.t >= fromMs && b.t <= toMs);

        let bars: typeof dailyBars;
        if (period === 'D') {
          bars = dailyBars;
        } else {
          // 주(W) / 월(M) 집계
          // 타임스탬프가 UTC 자정이므로 UTC 게터로 읽는다 — 로컬 게터를 쓰면
          // KST 기준 전날로 읽혀 월·주 경계가 하루씩 어긋난다.
          const groupKey = (ms: number): string => {
            const d = new Date(ms);
            if (period === 'M') return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}`;
            // 주: ISO week 단순 근사 — yyyy-WeekNo
            const onejan = Date.UTC(d.getUTCFullYear(), 0, 1);
            const week = Math.ceil(((ms - onejan) / 86400000 + new Date(onejan).getUTCDay() + 1) / 7);
            return `${d.getUTCFullYear()}-W${week}`;
          };
          const groups = new Map<string, typeof dailyBars>();
          for (const b of dailyBars) {
            const k = groupKey(b.t);
            if (!groups.has(k)) groups.set(k, []);
            groups.get(k)!.push(b);
          }
          bars = Array.from(groups.values()).map(g => ({
            t: g[g.length - 1].t,
            o: g[0].o,
            h: Math.max(...g.map(x => x.h)),
            l: Math.min(...g.map(x => x.l)),
            c: g[g.length - 1].c,
            v: g.reduce((s, x) => s + x.v, 0),
          })).sort((a, b) => a.t - b.t);
        }
        setData({
          candle: bars.map(b => [b.t, b.o, b.h, b.l, b.c]),
          volume: bars.map(b => [b.t, b.v]),
        });
      }
    } catch {
      setData({ candle: [], volume: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (prodId) fetchData(); /* eslint-disable-next-line */ }, [prodId, period, date]);

  /**
   * 주문·체결 표식 시리즈.
   *
   * 봉 간격에 맞춰 시각을 내림(floor)해 캔들과 x 좌표를 맞춘다 —
   * 5분봉에서 09:07 체결은 09:05 봉 위에 찍혀야 한다.
   * 일/주/월봉은 자정으로 내려 그날 캔들에 모은다.
   */
  const markerSeries = useMemo<Highcharts.SeriesScatterOptions[]>(() => {
    if (!markers?.length) return [];

    const stepMin = isIntraday ? (PERIOD_TO_MINUTE[period] ?? 1) : 0;

    const toEpoch = (s: string): number | null => {
      // "yyyy-MM-dd HH:mm:ss" / ISO 둘 다 받는다
      // 일/주/월봉은 **UTC 자정**으로 내린다 — `dayToEpoch` 가 캔들을 놓는 자리와
      // 같아야 표식이 그날 캔들 위에 앉는다. 예전에는 여기서도 KST→UTC(-9h)를
      // 빼서 10-02 체결이 10-01 캔들 위로 갔다 (2026-10-06 실측).
      const m = s.match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
      if (!m) {
        const d0 = s.match(/(\d{4})-(\d{2})-(\d{2})/);
        if (!d0) return null;
        return Date.UTC(+d0[1], +d0[2] - 1, +d0[3]);
      }
      const [, y, mo, dd, hh, mi] = m;
      if (!isIntraday) return Date.UTC(+y, +mo - 1, +dd);
      const floored = Math.floor(+mi / stepMin) * stepMin;
      return Date.UTC(+y, +mo - 1, +dd, +hh - 9, floored);
    };

    /**
     * 표식을 **그 시각을 담는 캔들** 위로 옮긴다.
     *
     * 주/월봉은 그룹의 **마지막 날** 타임스탬프를 쓴다(예: 10-02 주봉의 x 는
     * 10-02). 체결 시각을 그대로 두면 캔들 사이 빈 자리에 떠서, 표식이
     * 캔들에서 떨어져 보이거나 축 밖으로 나갔다 (2026-10-06 실측: 주/월봉
     * 표식 5건이 전부 캔들과 어긋남).
     *
     * 캔들 x 중 **ts 이상인 첫 값**으로 스냅한다 — 그게 그 거래를 포함한 봉이다.
     * 분봉은 toEpoch 가 이미 봉 시작으로 내림해서 정확히 일치하므로 그대로다.
     */
    const barXs = data.candle.map(c => c[0]);
    const snap = (ts: number): number => {
      if (barXs.length === 0) return ts;
      let lo = 0, hi = barXs.length - 1, ans = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (barXs[mid] >= ts) { ans = mid; hi = mid - 1; } else lo = mid + 1;
      }
      return ans === -1 ? barXs[barXs.length - 1] : barXs[ans];
    };

    const mk = (isBuy: boolean, isFill: boolean): Highcharts.SeriesScatterOptions => {
      const pts = markers
        .filter(x => x.isBuy === isBuy && x.isFill === isFill && x.prc != null && x.prc > 0)
        .map(x => {
          const ts = toEpoch(x.t);
          return ts == null ? null : {
            x: snap(ts), y: Number(x.prc),
            custom: { qty: x.qty ?? 0, note: x.note ?? '', when: x.t },
          };
        })
        .filter(Boolean) as Highcharts.PointOptionsObject[];

      return {
        type: 'scatter',
        name: `${isBuy ? '매수' : '매도'}${isFill ? '체결' : '주문'}`,
        data: pts,
        yAxis: 0,
        // 표식 색은 캔들(빨강/파랑)과 **겹치지 않게** 고른다 (2026-10-04 요청).
        //   매수 = 초록(#059669), 매도 = 주황(#ea580c)
        // 캔들이 적·청이라 같은 색을 쓰면 묻힌다. 초록/주황은 둘 다 캔들 팔레트에
        // 없고 서로도 충분히 달라 색각 이상에서도 구분된다(명도 차이도 있다).
        color: isBuy ? MARKER_BUY : MARKER_SELL,
        marker: {
          symbol: isBuy ? 'triangle' : 'triangle-down',
          radius: isFill ? 6 : 5,
          lineWidth: isFill ? 0 : 2,
          lineColor: isBuy ? MARKER_BUY : MARKER_SELL,
          fillColor: isFill ? undefined : 'rgba(255,255,255,0.9)',  // 주문은 빈 삼각형
        },
        // ⚠ Highstock 은 줌/팬 하면 series 를 다시 그리면서 **dataGrouping** 으로
        //   점을 묶거나 버린다. scatter 는 그 과정에서 표식이 사라진다
        //   (2026-10-04 사용자 보고: "확대하면 체결표시가 사라진다").
        //   표식은 개별 사건이라 묶으면 안 된다 — 그룹핑을 끈다.
        dataGrouping: { enabled: false },
        // 줌 밖의 점도 유지한다. 기본값이면 보이는 구간만 남겨 되돌아와도 안 돌아온다.
        cropThreshold: 0,
        // 표식 개수가 적으므로 항상 그린다 (기본은 많으면 자동 비활성)
        turboThreshold: 0,
        findNearestPointBy: 'xy',
        tooltip: {
          pointFormatter(this: any) {
            const c = this.options.custom || {};
            const q = c.qty ? ` ${Number(c.qty).toLocaleString()}주` : '';
            const n = c.note ? `<br/><span style="font-size:10px">${c.note}</span>` : '';
            return `<b>${this.series.name}</b> ${Number(this.y).toLocaleString()}원${q}`
                 + `<br/><span style="font-size:10px">${c.when ?? ''}</span>${n}`;
          },
        },
        zIndex: 5,
      } as Highcharts.SeriesScatterOptions;
    };

    // 빈 시리즈는 범례만 어지럽히므로 뺀다
    return [mk(true, true), mk(false, true), mk(true, false), mk(false, false)]
      .filter(s => (s.data?.length ?? 0) > 0);
  }, [markers, isIntraday, period, data]);

  const chartOptions: Highcharts.Options = useMemo(() => ({
    chart: {
      height, backgroundColor: '#ffffff',
      // 마우스 휠 확대/축소를 끈다 (2026-10-04 사용자 요청).
      // Highstock 기본값이 켜짐이라, 페이지를 스크롤하려다 차트 위에 커서가
      // 있으면 의도치 않게 확대·축소됐다. 드래그 줌도 함께 끈다 —
      // 기간은 1분/3분/…/월 버튼으로 바꾸는 것이 이 화면의 방식이다.
      zooming: { mouseWheel: { enabled: false }, type: undefined },
      panning: { enabled: false },
      pinchType: undefined,
    },
    title: { text: undefined },
    // 🔴 Highstock 은 점이 많으면 **dataGrouping** 으로 캔들을 묶는다.
    //   1분봉 381개를 요청해도 191개(2분 간격)로 뭉쳐 그려졌다
    //   (2026-10-06 실측: API 381건 → 차트 191pts @2분).
    //   "1분을 눌러도 안 바뀐다" 로 보인 원인이다. 전 시리즈에서 끈다.
    plotOptions: {
      series: { dataGrouping: { enabled: false } },
    },
    rangeSelector: { enabled: false },
    navigator: { enabled: false },
    scrollbar: { enabled: false },
    credits: { enabled: false },
    time: { timezone: 'Asia/Seoul' },
    xAxis: {
      type: 'datetime',
      labels: {
        formatter() {
          const d = new Date(this.value as number);
          const yy = String(d.getFullYear()).slice(2);        // "25"
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          if (period === 'M') return `${yy}.${mm}`;
          if (period === 'W' || period === 'D') return `${yy}.${mm}.${dd}`;
          // 분봉: 날짜 바뀌는 첫 틱은 날짜, 나머지는 HH:MM
          const hh = String(d.getHours()).padStart(2, '0');
          const min = String(d.getMinutes()).padStart(2, '0');
          return `${hh}:${min}`;
        },
      },
    },
    yAxis: [
      // 캔들 영역을 크게 잡는다. 분봉은 하루 안의 좁은 가격대라 더 키워야 움직임이 보인다.
      //   분봉(1m~60m) : 캔들 82% / 거래량 16%
      //   일·주·월봉    : 캔들 74% / 거래량 24%  (거래량 비교가 상대적으로 더 유용)
      { labels: { align: 'right', x: -3 }, height: isIntraday ? '82%' : '74%', resize: { enabled: true } },
      { labels: { align: 'right', x: -3 }, top: isIntraday ? '84%' : '76%',
        height: isIntraday ? '16%' : '24%', offset: 0 },
    ],
    tooltip: { split: true },
    legend: { enabled: true, align: 'left', verticalAlign: 'top', itemStyle: { fontSize: '10px' } },
    series: [
      { type: 'candlestick', name: prodNm || prodId, data: data.candle, yAxis: 0,
        color: '#1d4ed8', upColor: '#dc2626' } as Highcharts.SeriesCandlestickOptions,
      // 이동평균선 — 기본은 5/20/120 만 켜두고 나머지는 범례 클릭으로 켠다(선이 6개면 캔들이 가린다)
      ...MA_DEFS.map(m => ({
        type: 'line',
        name: `MA${m.period}`,
        data: calcSMA(data.candle, m.period),
        yAxis: 0,
        color: m.color,
        lineWidth: 1,
        visible: m.period === 5 || m.period === 20 || m.period === 120,
        marker: { enabled: false },
        tooltip: { valueDecimals: 0 },
        connectNulls: false,
      }) as Highcharts.SeriesLineOptions),
      { type: 'column', name: 'Volume', data: data.volume, yAxis: 1, color: '#94a3b8',
        showInLegend: false } as Highcharts.SeriesColumnOptions,
      // 주문·체결 표식 — markers 를 넘겼을 때만 추가된다.
      //   체결 = 채운 삼각형, 주문(미체결) = 빈 삼각형
      //   매수 = 위 삼각형(빨강), 매도 = 아래 삼각형(파랑)  ※ 한국 관행
      ...markerSeries,
    ],
  }), [data, prodNm, prodId, height, period, isIntraday, markerSeries]);

  const periods: { key: ChartPeriod; label: string }[] = [
    { key: '1m', label: '1분' },
    { key: '3m', label: '3분' },
    { key: '5m', label: '5분' },
    { key: '10m', label: '10분' },
    { key: '30m', label: '30분' },
    { key: '60m', label: '60분' },
    { key: 'D', label: '일' },
    { key: 'W', label: '주' },
    { key: 'M', label: '월' },
  ];

  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '4px 12px', fontSize: '12px', fontWeight: 600,
    backgroundColor: active ? '#3b82f6' : '#dbeafe',
    color: active ? 'white' : '#1e40af',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  });

  return (
    <div>
      {/* 한 줄: 기간 토글만 (날짜는 봉 종류별 자동) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px', flexWrap: 'wrap', fontSize: '11px' }}>
        {periods.map(p => (
          <button key={p.key} onClick={() => { setUserPickedPeriod(true); setPeriod(p.key); }} style={tabBtn(period === p.key)}>{p.label}</button>
        ))}
        {loading && <span style={{ marginLeft: 'auto', color: '#64748b' }}>로딩중...</span>}
      </div>

      <HighchartsReact highcharts={Highcharts} constructorType="stockChart" options={chartOptions} />
    </div>
  );
};

export default StockChart;
